import React from 'react';

import {assertAndContinue} from 'Asserts';
import OperatorDropdown from 'search/filters/base/OperatorDropdown';
import {Operator} from 'search/SearchConstants';
import {DateInput} from 'ui-library/inputs/DatetimeInput';
import {Size} from 'ui-library/inputs/InputConstants';
import pickDefined from 'util/pickDefined';

import {BASE_FILTER_FORM_PROP_NAMES, BaseFilterFormProps, FilterStyle} from './FilterFormConstants';
import FilterFormSkeletonNodeLayout from './FilterFormSkeletonNodeLayout';
import PrimaryFilterFormSkeleton from './PrimaryFilterFormSkeleton';
import SecondaryFilterFormSkeleton from './SecondaryFilterFormSkeleton';

interface DateFilterFormProps extends BaseFilterFormProps {
  filterStyle: FilterStyle;
  fromDate: string | null;
  onChangeToDate: (value: string) => void;
  onChangeFromDate: (value: string) => void;
  onRemove?: () => void;
  toDate: string | null;
  onChangeOperator?: (value: Operator) => void;
  operators?: Array<Operator>;
  selectedOperator?: Operator;
}

export default function DateFilterForm(props: DateFilterFormProps): JSX.Element {
  const skeletonProps = pickDefined(props, ...BASE_FILTER_FORM_PROP_NAMES);
  // Display operator dropdown if there are operators to choose from
  let operatorDropdown: JSX.Element | null = null;
  if (props.operators != null && props.operators.length > 1) {
    assertAndContinue(
      props.onChangeOperator != null,
      'Expected onChangeOperator to be defined when more than one operator is provided'
    );
    assertAndContinue(
      props.selectedOperator != null,
      'Expected selectedOperator to be defined when more than one operator is provided'
    );

    operatorDropdown =
      props.onChangeOperator != null && props.selectedOperator != null ? (
        <OperatorDropdown
          buttonSize={props.buttonSize}
          onChangeOperator={props.onChangeOperator}
          operators={props.operators}
          selectedOperator={props.selectedOperator}
        />
      ) : null;
  }

  const fromDateInput = (
    <DateInput
      onChange={props.onChangeFromDate}
      placeholder="from"
      size={Size.SMALL}
      value={props.fromDate ?? ''}
    />
  );
  const toDateInput = (
    <DateInput
      onChange={props.onChangeToDate}
      placeholder="to"
      size={Size.SMALL}
      value={props.toDate ?? ''}
    />
  );

  let nodeGrid: Array<Array<JSX.Element>>;
  if (operatorDropdown != null) {
    nodeGrid =
      props.selectedOperator === Operator.IS_EMPTY
        ? [[operatorDropdown]]
        : [[operatorDropdown], [fromDateInput, toDateInput]];
  } else {
    const emptyNode = <span />; // To get end date input to right-align beneath the start date input
    nodeGrid = [[fromDateInput], [emptyNode, toDateInput]];
  }
  if (props.filterStyle === FilterStyle.PRIMARY) {
    return (
      <PrimaryFilterFormSkeleton
        {...skeletonProps}
        content={<FilterFormSkeletonNodeLayout nodeGrid={[[fromDateInput], [toDateInput]]} />}
        dropdownToggleText={props.dropdownToggleText}
      />
    );
  } else {
    if (!props.onRemove) {
      throw new Error('Expected props.onRemove to be present when filterStyle is SECONDARY');
    }
    return (
      <SecondaryFilterFormSkeleton
        {...skeletonProps}
        nodeGrid={nodeGrid}
        onChangeFilterKey={props.onChangeFilterKey}
        selectedFilterKey={props.selectedFilterKey}
        onRemove={props.onRemove}
      />
    );
  }
}
